import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cronToPreset, presetToCron, scheduleProblem, upcomingRuns, WEEKDAY_NAMES, type NotifyMode, type SchedulePreset, type TriggerKind } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { browserTimezone, formatInZone, timezoneOptions } from '@/lib/triggers';
import type { FlowListItem, TestGroup, Trigger } from '@/lib/types';
import { Button, Dialog, Field, Input, Select, Toggle, useToast } from '@/components/ui';
import { NO_COLLECTION_LABEL } from '@/components/testdata/RunCollectionPicker';
import { collectionGroups } from '@/lib/runCollection';

const PRESET_OPTIONS = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Monday to Friday' },
  { value: 'weekly', label: 'Once a week' },
  { value: 'hourly', label: 'Every hour' },
  { value: 'custom', label: 'Custom (cron)' },
];

const pad = (n: number) => String(n).padStart(2, '0');

/** Create or edit a schedule or webhook that runs a flow or a group. */
export function TriggerDialog({
  projectId,
  kind,
  trigger,
  flows,
  groups,
  onClose,
  onCreated,
}: {
  projectId: string;
  kind: TriggerKind;
  trigger?: Trigger;
  flows: FlowListItem[];
  groups: TestGroup[];
  onClose: () => void;
  /** Called with the new trigger (a new webhook carries its token once). */
  onCreated?: (t: Trigger) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(trigger?.name ?? '');
  const [target, setTarget] = useState(trigger ? `${trigger.targetType}:${trigger.flowId ?? trigger.groupId}` : '');
  const [enabled, setEnabled] = useState(trigger?.enabled ?? true);
  const [notify, setNotify] = useState<NotifyMode>(trigger?.notify ?? 'FAILURES');
  const [collectionId, setCollectionId] = useState(trigger?.collectionId ?? '');
  const collections = useQuery({ queryKey: ['project-collections', projectId], queryFn: () => api.projectCollections(projectId) });
  const channels = useQuery({ queryKey: ['notification-channels', projectId], queryFn: () => api.notificationChannels(projectId) });
  const [preset, setPreset] = useState<SchedulePreset>(trigger?.cron ? cronToPreset(trigger.cron) : { kind: 'daily', hour: 2, minute: 0 });
  const [timezone, setTimezone] = useState(trigger?.timezone ?? browserTimezone());
  const zones = useMemo(() => timezoneOptions(), []);
  const [showErrors, setShowErrors] = useState(false);

  const cron = presetToCron(preset);
  const problem = kind === 'SCHEDULE' ? scheduleProblem(cron, timezone) : null;
  const upcoming = useMemo(() => {
    if (kind !== 'SCHEDULE' || problem) return [];
    try {
      return upcomingRuns(cron, timezone, new Date(), 3);
    } catch {
      return [];
    }
  }, [kind, cron, timezone, problem]);

  const targetOptions = [
    { value: '', label: flows.length || groups.length ? 'Choose a flow or group…' : 'Create a flow or a group first' },
    ...flows.map((f) => ({ value: `FLOW:${f.id}`, label: `Flow: ${f.name}${f.runnable ? '' : ' (needs work)'}` })),
    ...groups.map((g) => ({ value: `GROUP:${g.id}`, label: `Group: ${g.name}` })),
  ];

  const save = useMutation({
    mutationFn: () => {
      const [type, id] = target.split(':') as ['FLOW' | 'GROUP', string];
      const schedule = kind === 'SCHEDULE' ? { cron, timezone } : {};
      return trigger
        ? api.updateTrigger(trigger.id, { name: name.trim(), target: { type, id }, enabled, notify, collectionId: collectionId || null, ...schedule })
        : api.createTrigger(projectId, { name: name.trim(), kind, target: { type, id }, enabled, notify, collectionId: collectionId || null, ...schedule });
    },
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['triggers', projectId] });
      toast.success(trigger ? 'Saved' : kind === 'SCHEDULE' ? 'Schedule created' : 'Webhook created');
      if (!trigger) onCreated?.(t);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !target || problem) {
      setShowErrors(true);
      return;
    }
    save.mutate();
  };

  const timeValue = 'hour' in preset ? `${pad(preset.hour)}:${pad(preset.minute)}` : '02:00';
  const setTime = (v: string) => {
    const [h, m] = v.split(':').map(Number);
    if (Number.isFinite(h) && Number.isFinite(m) && 'hour' in preset) setPreset({ ...preset, hour: h!, minute: m! });
  };
  const changePreset = (k: string) => {
    const hour = 'hour' in preset ? preset.hour : 2;
    const minute = 'minute' in preset ? preset.minute : 0;
    if (k === 'hourly') setPreset({ kind: 'hourly', minute });
    else if (k === 'daily') setPreset({ kind: 'daily', hour, minute });
    else if (k === 'weekdays') setPreset({ kind: 'weekdays', hour, minute });
    else if (k === 'weekly') setPreset({ kind: 'weekly', day: 1, hour, minute });
    else setPreset({ kind: 'custom', cron });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={trigger ? `Edit ${kind === 'SCHEDULE' ? 'schedule' : 'webhook'}` : kind === 'SCHEDULE' ? 'New schedule' : 'New webhook'}
      description={
        kind === 'SCHEDULE'
          ? 'Runs a flow or a group automatically at the times you choose.'
          : 'Gives you a secret URL. Calling it (for example from GitLab after a deploy) runs a flow or a group.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="trigger-form" loading={save.isPending}>
            {trigger ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="trigger-form" onSubmit={submit} className="space-y-4">
        <Field label="Name" error={showErrors && !name.trim() ? 'Give it a name.' : undefined}>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus
              placeholder={kind === 'SCHEDULE' ? 'e.g. Nightly regression' : 'e.g. After staging deploy'}
            />
          )}
        </Field>
        <Field label="What runs" error={showErrors && !target ? 'Choose a flow or a group.' : undefined}>
          {(id) => <Select id={id} value={target} onChange={(e) => setTarget(e.target.value)} options={targetOptions} />}
        </Field>
        {(collections.data?.length ?? 0) > 0 && (
          <Field
            label="Test data"
            hint={kind === 'WEBHOOK' ? 'The environment its runs use. A call can pick another one with ?collection=<name>.' : 'The environment its runs use, e.g. Staging.'}
          >
            {(id) => (
              <Select
                id={id}
                value={collectionId}
                onChange={(e) => setCollectionId(e.target.value)}
                options={[{ value: '', label: NO_COLLECTION_LABEL }]}
                groups={collectionGroups(collections.data ?? [])}
              />
            )}
          </Field>
        )}

        {kind === 'SCHEDULE' && (
          <fieldset className="space-y-3 rounded-xl p-3 ring-1 ring-slate-200 dark:ring-slate-700">
            <legend className="px-1 text-sm font-medium text-slate-700 dark:text-slate-200">When</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Repeat">
                {(id) => <Select id={id} value={preset.kind} onChange={(e) => changePreset(e.target.value)} options={PRESET_OPTIONS} />}
              </Field>
              {preset.kind === 'weekly' && (
                <Field label="Day">
                  {(id) => (
                    <Select
                      id={id}
                      value={String(preset.day)}
                      onChange={(e) => setPreset({ ...preset, day: Number(e.target.value) })}
                      options={[1, 2, 3, 4, 5, 6, 0].map((d) => ({ value: String(d), label: WEEKDAY_NAMES[d]! }))}
                    />
                  )}
                </Field>
              )}
              {(preset.kind === 'daily' || preset.kind === 'weekdays' || preset.kind === 'weekly') && (
                <Field label="Time">{(id) => <Input id={id} type="time" value={timeValue} onChange={(e) => setTime(e.target.value)} required />}</Field>
              )}
              {preset.kind === 'hourly' && (
                <Field label="Minute past the hour">
                  {(id) => (
                    <Select
                      id={id}
                      value={String(preset.minute)}
                      onChange={(e) => setPreset({ kind: 'hourly', minute: Number(e.target.value) })}
                      options={Array.from({ length: 12 }, (_, i) => ({ value: String(i * 5), label: `:${pad(i * 5)}` }))}
                    />
                  )}
                </Field>
              )}
              {preset.kind === 'custom' && (
                <Field label="Cron" hint="minute hour day-of-month month day-of-week, e.g. 0 */6 * * *">
                  {(id) => <Input id={id} value={preset.cron} onChange={(e) => setPreset({ kind: 'custom', cron: e.target.value })} className="font-mono" />}
                </Field>
              )}
              <Field label="Timezone">
                {(id) => <Select id={id} value={timezone} onChange={(e) => setTimezone(e.target.value)} options={zones.map((z) => ({ value: z, label: z }))} />}
              </Field>
            </div>
            {problem ? (
              <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">
                {problem}
              </p>
            ) : (
              upcoming.length > 0 && (
                <p className="text-sm text-slate-500">
                  Next runs: {upcoming.map((d) => formatInZone(d, timezone)).join(' · ')}
                  {timezone !== browserTimezone() && ` (${timezone})`}
                </p>
              )
            )}
          </fieldset>
        )}

        <Field
          label="Notify"
          hint={
            channels.data?.length
              ? `Sent to ${channels.data.map((c) => c.name).join(', ')}.`
              : 'No Slack, Teams, Zoho Cliq or email channel is connected yet - a Project Admin connects them on the Plugins page.'
          }
        >
          {(id) => (
            <Select
              id={id}
              value={notify}
              onChange={(e) => setNotify(e.target.value as NotifyMode)}
              options={[
                { value: 'FAILURES', label: 'When a run fails, and when it passes again' },
                { value: 'ALWAYS', label: 'After every run' },
                { value: 'OFF', label: 'Never' },
              ]}
            />
          )}
        </Field>

        <div className="flex items-start gap-3">
          <Toggle checked={enabled} onChange={setEnabled} label="Turned on" />
          <div className="text-sm">
            <div className="font-medium">Turned on</div>
            <div className="text-xs text-slate-500">
              {kind === 'SCHEDULE' ? 'Off: the schedule keeps its settings but does not run.' : 'Off: calls to the URL are refused.'}{' '}
              {trigger
                ? `Runs start as ${trigger.createdBy?.name ?? 'its creator'}.`
                : 'Runs start as you, so you need to be allowed to run tests in this project.'}
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
